import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Heading, Line, Link, Button, Spinner } from '@mm/components';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';
import { CheckCircleIcon } from '@mm/icons/general';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { useKeyDown } from '../../../../common/hooks/use-key-down';
import { useEmailVerificationReducer } from './use-email-verification-reducer';
import message from './message.png';
import css from './VerificationForm.module.css';
import TextInput from '../../../../common/components/TextInput';

type Props = {
  className?: string;
  inputsOnly?: boolean;
  stylings?: {
    card?: string;
    verifyButtonsContainer?: string;
    resendContainer?: string;
    primaryButton?: string;
    paragraph?: string;
  };
};

const getErrorText = (error: any) => {
  if (error === 1303) {
    return (
      <>
        Sorry, that code is invalid.
        <br />
        Click the “Resend” bellow to get a new code.
      </>
    );
  }

  if (error?.status === 429) {
    return (
      <>
        Too many attempts.
        <br />
        Please try requesting new code later.
      </>
    );
  }

  return (
    <>
      Something went wrong
      <br />
      Click the “Resend” bellow to get a new code.
    </>
  );
};

export const VerificationForm = ({
  className,
  inputsOnly,
  stylings,
}: Props) => {
  const { email = '' } = useSelector(selectCurrentUser) || {};
  const emailHref = `mailto:${email}`;

  const { state, dispatcher } = useEmailVerificationReducer();
  const { busy, verifySuccess, resendSuccess, error } = state;

  useKeyDown((e: KeyboardEvent) => {
    if (e.key === ENTER_KEY_NAME) {
      dispatcher.verify();
    }
  });

  return (
    <div className={classNames(className, css.container)}>
      <div className={classNames(css.card, stylings?.card)}>
        {!inputsOnly && (
          <div>
            <img className={css.image} src={message} alt="Customers.ai" />

            <Heading className={css.heading} size={4}>
              We need to verify your email
            </Heading>

            <p className={css.paragraph}>
              We sent an email to the following address:
              <Link className={css.email} href={emailHref} capitalize={false}>
                {email}
              </Link>
            </p>

            <Line className={css.line} />
          </div>
        )}
        {busy && (
          <>
            <Heading className={css.heading} size={4}>
              Hold on...
            </Heading>
            <Spinner standalone className={css.loader} />
          </>
        )}

        {verifySuccess && !busy && (
          <>
            <Heading className={css.heading} size={4}>
              Email verified!
            </Heading>
            <CheckCircleIcon />
          </>
        )}

        {!busy && !verifySuccess && (
          <>
            {!inputsOnly && (
              <p className={css.paragraph}>
                Check your email to get your verification code. You may need to
                check your spam folder.
              </p>
            )}

            <div
              className={classNames(
                css.verify,
                stylings?.verifyButtonsContainer,
              )}
            >
              <TextInput
                className={css.input}
                placeholder="Enter your code here"
                value={state.verificationCode}
                onChange={e => {
                  dispatcher.updateVerificationCode(e.target.value);
                }}
              />
              <Button
                css={{ container: css.button }}
                onClick={dispatcher.verify}
                disabled={busy}
              >
                Verify
              </Button>
            </div>

            {error && (
              <Heading size={4} className={classNames(css.paragraph, css.warn)}>
                {getErrorText(error)}
              </Heading>
            )}

            {resendSuccess && (
              <>
                <Heading className={css.heading} size={4}>
                  Code sent. Please check you email
                </Heading>
                <CheckCircleIcon />
              </>
            )}

            {!resendSuccess && (
              <div
                className={classNames(css.resend, stylings?.resendContainer)}
              >
                <p className={classNames(css.paragraph, stylings?.paragraph)}>
                  Don't see email?
                </p>

                <Button
                  css={{
                    container: classNames(css.button, stylings?.primaryButton),
                  }}
                  onClick={dispatcher.resend}
                  disabled={busy}
                >
                  Resend
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
